import type {
  Shape,
  WallPathShape,
  WallShape,
  ZoneShape
} from "@gettactic/map-builder/src/types";
import { autoMergeWallPaths } from "@gettactic/map-builder/src/utils/autoMergeWallPaths";
import { snapToWallEndpoint } from "@gettactic/map-builder/src/utils/snap";
import {
  bendWallPathEdge,
  curvatureFromPoint,
  insertWallPathVertex,
  moveWallEndpoint,
  moveWallPathVertex,
  moveZoneVertex,
  removeWallPathVertex,
  setWallPathEdgeCurvature,
  setZoneEdgeCurvature,
  splitWallPathAtEdge,
  splitWallPathAtVertex
} from "../document/geometryEdits";
import { addShapes, deleteShapes, updateShape } from "../document/operations";
import { documentFromShapes, orderedShapes } from "../document/serialize";
import type { Point } from "../types";
import type { ToolContext } from "./Tool";

/** `data-handle` values of the geometry handles: `vertex:i`, `midpoint:i`,
 *  `curve:i`, `wall-start`, `wall-end`, `door:edge:id`, `window:edge:id`. */
export type GeometryHandle =
  | { kind: "vertex"; index: number }
  | { kind: "midpoint"; index: number }
  | { kind: "curve"; index: number }
  | { kind: "wall-end"; end: "start" | "end" }
  /** A door or window: `edge` is the wall-path edge (0 for a wall). */
  | { kind: "opening"; opening: "doors" | "windows"; edge: number; id: string };

export const parseGeometryHandle = (handle: string): GeometryHandle | null => {
  if (handle === "wall-start") return { kind: "wall-end", end: "start" };
  if (handle === "wall-end") return { kind: "wall-end", end: "end" };
  const [kind, index, id] = handle.split(":");
  if (
    (kind === "vertex" || kind === "midpoint" || kind === "curve") &&
    index !== undefined
  ) {
    return { kind, index: Number(index) };
  }
  if ((kind === "door" || kind === "window") && index !== undefined && id) {
    return {
      kind: "opening",
      opening: kind === "door" ? "doors" : "windows",
      edge: Number(index),
      id
    };
  }
  return null;
};

/** Merge magnet radius for Cmd-dragging an endpoint, in screen pixels. */
const MERGE_RADIUS_PX = 30;

/** A drag of one geometry handle: live preview via the shape's own store
 *  update without history, one history entry on release. */
export interface GeometryDrag {
  shapeId: string;
  handle: GeometryHandle;
  /** Document before the drag, restored under the final commit so the
   *  whole drag is one undo step. */
  before: {
    shapes: Record<string, Shape>;
    order: string[];
    selection: string[];
  };
  /** Set while Cmd-dragging an endpoint onto another open path's end. */
  mergeTarget?: Point | null;
}

export interface DragModifiers {
  altKey: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
}

/** Endpoints of other open wall paths, the targets a Cmd-drag can merge into. */
const mergeCandidates = (
  context: ToolContext,
  path: WallPathShape
): Point[] => {
  const points: Point[] = [];
  for (const other of Object.values(context.store.getState().shapes)) {
    if (other.type !== "wall-path" || other.id === path.id) continue;
    if (other.closed || other.vertices.length < 2) continue;
    points.push(other.vertices[0], other.vertices[other.vertices.length - 1]);
  }
  return points;
};

const openingPatch = (
  shape: WallShape | WallPathShape,
  handle: Extract<GeometryHandle, { kind: "opening" }>,
  world: Point
): Partial<Shape> | null => {
  const from =
    shape.type === "wall"
      ? { x: shape.x, y: shape.y }
      : shape.vertices[handle.edge];
  const to =
    shape.type === "wall"
      ? { x: shape.x2, y: shape.y2 }
      : shape.vertices[(handle.edge + 1) % shape.vertices.length];
  if (!from || !to) return null;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return null;
  // Keep the whole opening on the wall, as the Konva editor did.
  const length = Math.sqrt(lengthSquared);
  const current =
    shape.type === "wall"
      ? shape[handle.opening]?.find((entry) => entry.id === handle.id)
      : shape.edges[handle.edge]?.[handle.opening]?.find(
          (entry) => entry.id === handle.id
        );
  const halfWidth = (current?.width ?? 0) / 2;
  const along = ((world.x - from.x) * dx + (world.y - from.y) * dy) / length;
  const minAlong = Math.min(halfWidth, length / 2);
  const position =
    Math.max(minAlong, Math.min(length - minAlong, along)) / length;
  const move = <T extends { id: string; position: number }>(
    list: T[] | undefined
  ): T[] =>
    (list ?? []).map((entry) =>
      entry.id === handle.id ? { ...entry, position } : entry
    );
  if (shape.type === "wall") {
    return {
      [handle.opening]: move(shape[handle.opening])
    } as Partial<WallShape>;
  }
  const edges = [...shape.edges];
  edges[handle.edge] = {
    ...edges[handle.edge],
    [handle.opening]: move(edges[handle.edge]?.[handle.opening])
  };
  return { edges } as Partial<WallPathShape>;
};

const patchForDrag = (
  context: ToolContext,
  shape: Shape,
  handle: GeometryHandle,
  world: Point,
  modifiers: DragModifiers,
  drag?: GeometryDrag
): { patch: Partial<Shape>; guides?: { x?: number; y?: number } } | null => {
  if (handle.kind === "opening") {
    if (shape.type !== "wall" && shape.type !== "wall-path") return null;
    const patch = openingPatch(shape, handle, world);
    return patch ? { patch } : null;
  }
  if (shape.type === "wall-path") {
    if (handle.kind === "vertex" || handle.kind === "midpoint") {
      const isEndpoint =
        handle.index === 0 || handle.index === shape.vertices.length - 1;
      if (
        (modifiers.metaKey || modifiers.ctrlKey) &&
        isEndpoint &&
        !shape.closed &&
        drag
      ) {
        // Cmd-drag: magnet onto another open path's end; the drop merges.
        const radius =
          MERGE_RADIUS_PX /
          Math.max(context.store.getState().viewport.scale, 0.1);
        let target: Point | null = null;
        let best = radius;
        for (const candidate of mergeCandidates(context, shape)) {
          const distance = Math.hypot(
            candidate.x - world.x,
            candidate.y - world.y
          );
          if (distance < best) {
            best = distance;
            target = candidate;
          }
        }
        drag.mergeTarget = target;
        return {
          patch: moveWallPathVertex(shape, handle.index, target ?? world)
        };
      }
      if (drag) drag.mergeTarget = null;
      const snapped = modifiers.altKey
        ? { point: world }
        : snapVertex(context, shape, handle.index, world);
      return {
        patch: moveWallPathVertex(shape, handle.index, snapped.point),
        guides: snapped.guides
      };
    }
    if (handle.kind === "curve") {
      const from = shape.vertices[handle.index];
      const to = shape.vertices[(handle.index + 1) % shape.vertices.length];
      const curve = curvatureFromPoint(from, to, world);
      return curve
        ? { patch: setWallPathEdgeCurvature(shape, handle.index, curve) }
        : null;
    }
    return null;
  }
  if (shape.type === "zone") {
    const vertices = (shape.vertices ?? []).map((vertex) => ({
      x: shape.x + vertex.x,
      y: shape.y + vertex.y
    }));
    if (handle.kind === "vertex") {
      return {
        patch: moveZoneVertex(
          shape,
          handle.index,
          context.snap.snapPoint(world)
        )
      };
    }
    if (handle.kind === "curve") {
      const from = vertices[handle.index];
      const to = vertices[(handle.index + 1) % vertices.length];
      const curve = curvatureFromPoint(from, to, world);
      return curve
        ? { patch: setZoneEdgeCurvature(shape, handle.index, curve) }
        : null;
    }
    return null;
  }
  if (shape.type === "wall") {
    if (handle.kind === "wall-end") {
      const snapped = modifiers.altKey
        ? { x: world.x, y: world.y, alignX: undefined, alignY: undefined }
        : snapToWallEndpoint(
            context.snap.snapPoint(world),
            Object.values(context.store.getState().shapes).filter(
              (other) => other.id !== shape.id
            ),
            context.store.getState().viewport.scale
          );
      return {
        patch: moveWallEndpoint(handle.end, snapped),
        guides:
          snapped.alignX !== undefined || snapped.alignY !== undefined
            ? { x: snapped.alignX, y: snapped.alignY }
            : undefined
      };
    }
    if (handle.kind === "curve") {
      const curve = curvatureFromPoint(
        { x: shape.x, y: shape.y },
        { x: shape.x2, y: shape.y2 },
        world
      );
      return curve ? { patch: curve as Partial<WallShape> } : null;
    }
  }
  return null;
};

/** Other vertices of the path (and other walls) are snap targets. */
const snapVertex = (
  context: ToolContext,
  path: WallPathShape,
  index: number,
  world: Point
): { point: Point; guides?: { x?: number; y?: number } } => {
  const others = path.vertices.filter((_, current) => current !== index);
  const state = context.store.getState();
  const snapped = snapToWallEndpoint(
    context.snap.snapPoint(world),
    Object.values(state.shapes).filter((other) => other.id !== path.id),
    state.viewport.scale,
    others
  );
  return {
    point: { x: snapped.x, y: snapped.y },
    guides:
      snapped.alignX !== undefined || snapped.alignY !== undefined
        ? { x: snapped.alignX, y: snapped.alignY }
        : undefined
  };
};

/** Starts a handle drag; a midpoint press first inserts the vertex it will drag. */
export const beginGeometryDrag = (
  context: ToolContext,
  shapeId: string,
  handle: GeometryHandle
): GeometryDrag | null => {
  const state = context.store.getState();
  const shape = state.shapes[shapeId];
  if (!shape) return null;
  const before = {
    shapes: state.shapes,
    order: state.order,
    selection: state.selection
  };
  let dragHandle = handle;
  if (shape.type === "wall-path" && handle.kind === "midpoint") {
    const inserted = insertWallPathVertex(shape, handle.index);
    state.updateShape(shapeId, inserted.patch, { history: false });
    dragHandle = { kind: "vertex", index: inserted.vertexIndex };
  }
  return { shapeId, handle: dragHandle, before };
};

export const updateGeometryDrag = (
  context: ToolContext,
  drag: GeometryDrag,
  world: Point,
  modifiers: DragModifiers
): void => {
  const shape = context.store.getState().shapes[drag.shapeId];
  if (!shape) return;
  const result = patchForDrag(
    context,
    shape,
    drag.handle,
    world,
    modifiers,
    drag
  );
  if (!result) return;
  context.store
    .getState()
    .updateShape(drag.shapeId, result.patch, { history: false });
  context.overlay.set({ guides: result.guides ?? null });
};

/** Ends the drag: the document before it becomes the undo entry. */
export const endGeometryDrag = (
  context: ToolContext,
  drag: GeometryDrag
): void => {
  const store = context.store;
  context.overlay.set({ guides: null });
  if (drag.mergeTarget) {
    // Dropped on another path's end: join the two into one path.
    const merged = autoMergeWallPaths(orderedShapes(store.getState()));
    store
      .getState()
      .commit(() => documentFromShapes(merged), { history: false });
  }
  const after = store.getState();
  if (after.shapes === drag.before.shapes) return;
  store.setState({
    history: { past: [...after.history.past, drag.before], future: [] },
    isDirty: true
  });
};

export const cancelGeometryDrag = (
  context: ToolContext,
  drag: GeometryDrag
): void => {
  context.store.setState({
    shapes: drag.before.shapes,
    order: drag.before.order
  });
  context.overlay.set({ guides: null });
};

/** Clicks on handles that edit rather than drag: select a wall-path vertex,
 *  Alt-click inserts one next to it, Shift-click a midpoint bends the edge. */
export const clickGeometryHandle = (
  context: ToolContext,
  shapeId: string,
  handle: GeometryHandle,
  modifiers: { altKey: boolean; shiftKey: boolean }
): boolean => {
  const state = context.store.getState();
  const shape = state.shapes[shapeId];
  if (shape?.type !== "wall-path") return false;
  if (handle.kind === "vertex") {
    if (modifiers.altKey) {
      const isLast =
        !shape.closed && handle.index === shape.vertices.length - 1;
      const edgeIndex = isLast ? handle.index - 1 : handle.index;
      state.updateShape(shapeId, insertWallPathVertex(shape, edgeIndex).patch);
      return true;
    }
    state.setChrome({ wallPathSelectedVertexIndex: handle.index });
    return true;
  }
  if (handle.kind === "midpoint" && modifiers.shiftKey) {
    state.updateShape(shapeId, bendWallPathEdge(shape, handle.index));
    return true;
  }
  return false;
};

/** Double-click: remove a wall-path vertex (Alt: split the path there);
 *  reset an edge's curvature. */
export const doubleClickGeometryHandle = (
  context: ToolContext,
  shapeId: string,
  handle: GeometryHandle,
  modifiers: { altKey: boolean }
): void => {
  const state = context.store.getState();
  const shape = state.shapes[shapeId];
  if (!shape) return;
  if (handle.kind === "curve") {
    if (shape.type === "wall-path") {
      state.updateShape(
        shapeId,
        setWallPathEdgeCurvature(shape, handle.index, {
          curvature: 0,
          curvatureCenter: 0.5
        })
      );
    } else if (shape.type === "zone") {
      state.updateShape(
        shapeId,
        setZoneEdgeCurvature(shape, handle.index, {
          curvature: 0,
          curvatureCenter: 0.5
        })
      );
    } else if (shape.type === "wall") {
      state.updateShape(shapeId, {
        curvature: 0,
        curvatureCenter: 0.5
      } as Partial<WallShape>);
    }
    return;
  }
  if (shape.type !== "wall-path" || handle.kind !== "vertex") return;
  if (modifiers.altKey) {
    replacePath(context, shape, splitWallPathAtVertex(shape, handle.index));
    return;
  }
  const patch = removeWallPathVertex(shape, handle.index);
  if (patch) {
    state.updateShape(shapeId, patch);
  } else {
    state.commit((document) => deleteShapes(document, [shapeId]), {
      selection: []
    });
  }
};

const OPENING_WIDTH_STEP = 5;
const OPENING_MIN_WIDTH = 5;
const OPENING_MAX_WIDTH = 300;

/** Wheel over a door or window: grow or shrink it in 5px steps. */
export const resizeOpening = (
  context: ToolContext,
  shapeId: string,
  handle: Extract<GeometryHandle, { kind: "opening" }>,
  direction: 1 | -1
): boolean => {
  const state = context.store.getState();
  const shape = state.shapes[shapeId];
  if (shape?.type !== "wall" && shape?.type !== "wall-path") return false;
  const resize = <T extends { id: string; width: number }>(
    list: T[] | undefined
  ): T[] =>
    (list ?? []).map((entry) =>
      entry.id === handle.id
        ? {
            ...entry,
            width: Math.max(
              OPENING_MIN_WIDTH,
              Math.min(
                OPENING_MAX_WIDTH,
                entry.width + direction * OPENING_WIDTH_STEP
              )
            )
          }
        : entry
    );
  if (shape.type === "wall") {
    state.updateShape(shapeId, {
      [handle.opening]: resize(shape[handle.opening])
    } as Partial<WallShape>);
    return true;
  }
  const edges = [...shape.edges];
  edges[handle.edge] = {
    ...edges[handle.edge],
    [handle.opening]: resize(edges[handle.edge]?.[handle.opening])
  };
  state.updateShape(shapeId, { edges } as Partial<WallPathShape>);
  return true;
};

/** Right-click on a midpoint splits the path at that edge. */
export const contextMenuGeometryHandle = (
  context: ToolContext,
  shapeId: string,
  handle: GeometryHandle
): boolean => {
  const shape = context.store.getState().shapes[shapeId];
  if (shape?.type !== "wall-path" || handle.kind !== "midpoint") return false;
  replacePath(context, shape, splitWallPathAtEdge(shape, handle.index));
  return true;
};

const replacePath = (
  context: ToolContext,
  path: WallPathShape,
  replacement: Shape[]
) => {
  context.store
    .getState()
    .commit(
      (document) => addShapes(deleteShapes(document, [path.id]), replacement),
      { selection: replacement.map((shape) => shape.id) }
    );
};

export const zoneVerticesWorld = (zone: ZoneShape): Point[] =>
  (zone.vertices ?? []).map((vertex) => ({
    x: zone.x + vertex.x,
    y: zone.y + vertex.y
  }));

export { updateShape };
